/**
 * EntityCard - A richer entity card for v2 AI action cards.
 *
 * Shows entity type icon, label, optional metadata rows, and lifecycle badge.
 * Optionally clickable to open the CRM slideout panel.
 *
 * Note: metadata is pre-computed by the calling card component — EntityCard
 * itself has no CRM domain logic.
 */

import type { ReactNode } from 'react';

import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons/EntityTypeIcon';
import { cx } from '@/utils/cx';

import { useClickableEntity } from './shared/useClickableEntity';
import { type ActionVariant, VARIANT_CARD } from './shared/variants';

export interface EntityCardMetadataItem {
  icon?: ReactNode;
  label: string;
  /** Unique key for React reconciliation. Falls back to label if omitted. */
  key?: string;
}

export interface EntityCardProps {
  /** CRM entity type name (e.g., 'ahoy_contact', 'ahoy_deal') */
  entityTypeName: string;
  /** Display label for the entity */
  label: string;
  /** Optional explicit icon name from @untitledui/icons (overrides entityTypeName-based icon) */
  iconName?: string;
  /** Pre-computed metadata rows — extracted by the calling card component */
  metadata?: EntityCardMetadataItem[];
  /** Optional lifecycle stage badge rendered below the label */
  badge?: ReactNode;
  /** Visual variant — affects background and border color */
  variant?: ActionVariant;
  /** When provided, clicking the card opens the CRM slideout for this entity */
  entityId?: string;
  className?: string;
}

/**
 * Richer entity card used in v2 AI action cards.
 *
 * Extends ObjectMiniCard with support for multiple metadata rows and a
 * lifecycle badge. Metadata is pre-computed externally — this component has
 * no CRM domain logic.
 */
export function EntityCard({
  entityTypeName,
  label,
  iconName,
  metadata,
  badge,
  variant = 'default',
  entityId,
  className,
}: EntityCardProps) {
  const Icon = getEntityTypeIconComponent(entityTypeName, iconName);
  const { isClickable, containerProps } = useClickableEntity(entityId);

  return (
    <div
      {...containerProps}
      className={cx(
        'flex items-start gap-2.5 px-3 py-2.5 rounded-md border',
        VARIANT_CARD[variant],
        isClickable &&
          'group cursor-pointer hover:border-border-primary transition-colors',
        className
      )}
    >
      {/* Entity type icon */}
      <Icon
        className='w-4 h-4 text-fg-secondary shrink-0 mt-0.5'
        aria-hidden='true'
      />

      {/* Content */}
      <div className='min-w-0 flex-1'>
        {/* Label */}
        <div
          className={cx(
            'text-sm font-medium text-fg-primary truncate',
            isClickable && 'group-hover:underline'
          )}
        >
          {label}
        </div>

        {/* Badge */}
        {badge && <div className='mt-1'>{badge}</div>}

        {/* Metadata rows */}
        {metadata && metadata.length > 0 && (
          <div className='mt-1.5 space-y-1'>
            {metadata.map(item => (
              <div
                key={item.key ?? item.label}
                className='flex items-center gap-1.5 text-xs text-fg-tertiary'
              >
                {item.icon && (
                  <span className='w-3.5 h-3.5 shrink-0 flex items-center justify-center'>
                    {item.icon}
                  </span>
                )}
                <span className='truncate'>{item.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
